import type { AdminSelf } from '@saay/api-client';
import { formatCount } from '@saay/formatting';
import type { IconName } from './kit.js';

import { type AdminArea, canSeeArea, type PhysicalCardsTab } from './permissions.js';

/**
 * Where the admin prototype puts a destination (D-48):
 * - `sidebar`: the prototype sidebar, in its order, and «بیشتر» at phone width unless in the bar.
 * - `more`: only the phone «بیشتر» sheet; the prototype has no sidebar item for it.
 * - `inside`: opened from another screen, never from the navigation (categories from فروشگاه‌ها).
 */
export type AdminPlacement = 'sidebar' | 'more' | 'inside';

/** The prototype heading of a screen, also used by its «به‌زودی» page. */
export interface ScreenHeading {
  readonly description?: string;
  readonly eyebrow?: string;
  readonly title: string;
}

/** The admin information architecture (FTD §8) in the prototype's navigation. */
export interface AdminRoute {
  readonly area: AdminArea | 'me';
  readonly heading: ScreenHeading;
  /** The prototype icon (admin-web prototype `a-*` sprite, D-48). */
  readonly icon: IconName;
  readonly labelFa: string;
  readonly path: string;
  readonly placement: AdminPlacement;
  /** The destination has a shipped screen (Q9: unready routes stay out of navigation). */
  readonly ready?: boolean;
  /** The label in the phone bottom navigation. */
  readonly shortLabelFa?: string;
}

export const ADMIN_ROUTES: readonly AdminRoute[] = [
  {
    area: 'dashboard',
    heading: {
      description: 'اعتبار، ظرفیت پذیرندگان، وصول، تسویه و ریسک عملیاتی.',
      eyebrow: 'نظارت و کنترل',
      title: 'داشبورد پلتفرم',
    },
    icon: 'home',
    labelFa: 'داشبورد',
    path: '/dashboard',
    placement: 'sidebar',
    ready: true,
    shortLabelFa: 'خانه',
  },
  {
    area: 'approvals',
    heading: {
      description:
        'تغییرهای مالی حساس تا تأیید یک مدیر دیگر اعمال نمی‌شوند؛ هیچ‌کس درخواست خودش را تأیید نمی‌کند.',
      title: 'درخواست‌ها',
    },
    icon: 'check',
    labelFa: 'درخواست‌ها',
    path: '/approvals',
    placement: 'sidebar',
    ready: true,
    shortLabelFa: 'درخواست‌ها',
  },
  {
    area: 'merchants',
    heading: {
      description: 'پروفایل، شعبه‌ها، ظرفیت، نرخ SAAY و شرایط اقساط هر فروشگاه.',
      eyebrow: 'پذیرندگان',
      title: 'مدیریت فروشگاه‌ها',
    },
    icon: 'store',
    labelFa: 'فروشگاه‌ها',
    path: '/merchants',
    placement: 'sidebar',
    ready: true,
    shortLabelFa: 'فروشگاه',
  },
  {
    area: 'organizations',
    heading: {
      description:
        'هر سازمان یک نمونه مستقل است؛ نقش‌های سازمانی سراسری‌اند و اعتبار اعضا روی همان کارت واحد SAAY قرار می‌گیرد.',
      eyebrow: 'مدیریت مستقیم توسط ادمین',
      title: 'سازمان‌ها، اعضا و اعتبار',
    },
    icon: 'users',
    labelFa: 'سازمان‌ها',
    path: '/organizations',
    placement: 'sidebar',
    ready: true,
  },
  {
    area: 'buyers',
    heading: {
      description:
        'ادمین خریدار و اعتبار اولیه را ثبت می‌کند؛ خریدار سپس با کد پیامکی وارد برنامه می‌شود.',
      eyebrow: 'ساخت حساب فقط توسط ادمین',
      title: 'خریداران و اعتبار',
    },
    icon: 'users',
    labelFa: 'خریداران و اعتبار',
    path: '/buyers',
    placement: 'sidebar',
    ready: true,
  },
  // D-117: everything card-related in one place (summary, cities, numbering, print, history).
  {
    area: 'physicalCards',
    heading: { title: 'کارت‌های فیزیکی' },
    icon: 'card',
    labelFa: 'کارت‌های فیزیکی',
    path: '/physical-cards',
    placement: 'sidebar',
    ready: true,
  },
  {
    area: 'creditPrograms',
    heading: {
      description:
        'برنامه را یک‌بار تعریف کنید؛ سپس یک فرد یا یک گروه را با نقش سراسری و فیلترهای اختیاری سازمان و منطقه هدف بگیرید.',
      eyebrow: 'یک کارت، چند منبع اعتبار حسابرسی‌شده',
      title: 'برنامه‌ها و اعتبار اضافی',
    },
    icon: 'card',
    labelFa: 'برنامه‌های اعتبار اضافی',
    path: '/credit-programs',
    placement: 'sidebar',
    ready: true,
  },
  {
    area: 'sales',
    heading: {
      description: 'شواهد تأیید، نسخهٔ ثبت‌شدهٔ قرارداد، برنامه اقساط و اصلاحیه مرجوعی.',
      eyebrow: 'سوابق غیرقابل حذف',
      title: 'فروش‌ها و قراردادها',
    },
    icon: 'doc',
    labelFa: 'فروش‌ها و قراردادها',
    path: '/sales',
    placement: 'sidebar',
  },
  {
    area: 'payments',
    heading: { eyebrow: 'مسئولیت SAAY', title: 'اقساط، پرداخت و دیرکرد' },
    icon: 'clock',
    labelFa: 'اقساط و وصول',
    path: '/payments',
    placement: 'sidebar',
    ready: true,
  },
  {
    area: 'wallets',
    heading: { eyebrow: 'موجودی و گردش', title: 'کیف پول' },
    icon: 'card',
    labelFa: 'کیف پول',
    path: '/wallets',
    placement: 'sidebar',
    ready: true,
  },
  {
    area: 'wallets',
    heading: { eyebrow: 'خروج وجه', title: 'برداشت‌ها' },
    icon: 'bank',
    labelFa: 'برداشت‌ها',
    path: '/wallets/withdrawals',
    placement: 'inside',
    ready: true,
  },
  {
    area: 'wallets',
    heading: { eyebrow: 'انتقال بین خریداران', title: 'انتقال‌های بزرگ' },
    icon: 'share',
    labelFa: 'انتقال‌های بزرگ',
    path: '/wallets/transfers',
    placement: 'inside',
    ready: true,
  },
  {
    area: 'settlements',
    heading: { eyebrow: 'تعهد مستقل از وصول خریدار', title: 'تسویهٔ فروشگاه‌ها' },
    icon: 'bank',
    labelFa: 'تسویه‌ها',
    path: '/settlements',
    placement: 'sidebar',
    shortLabelFa: 'تسویه',
  },
  {
    area: 'marketing',
    heading: {
      description:
        'قانون کمیسیون مستقل از افراد تعریف می‌شود و سپس نسخهٔ آن به فروشگاه و بازاریاب‌های هر لایه متصل می‌شود.',
      eyebrow: 'ساختار، افراد و تخصیص فروشگاه',
      title: 'مدیریت بازاریابی',
    },
    icon: 'share',
    labelFa: 'مدیریت بازاریابی',
    path: '/marketing',
    placement: 'sidebar',
    ready: true,
  },
  {
    area: 'policies',
    heading: {
      description:
        'سیاست‌ها نسخه‌دار و تاریخ‌دارند. قراردادها مقادیر زمان تأیید را نگه می‌دارند و با نسخه جدید تغییر نمی‌کنند.',
      eyebrow: 'قابل تنظیم توسط ادمین',
      title: 'سیاست‌ها',
    },
    icon: 'settings',
    labelFa: 'سیاست‌ها',
    path: '/policies',
    placement: 'sidebar',
    ready: true,
  },
  {
    area: 'platformAccess',
    heading: {
      title: 'دسترسی‌ها',
    },
    icon: 'shield',
    labelFa: 'دسترسی‌ها',
    path: '/platform-access',
    placement: 'sidebar',
    ready: true,
  },
  {
    area: 'rolesAuditLedger',
    heading: {
      description: 'ردیابی انجام‌دهنده، دلیل، مقادیر قبل و بعد و ثبت‌های مالی دوبل.',
      eyebrow: 'تاریخچه تغییرناپذیر',
      title: 'حسابرسی و دفترکل',
    },
    icon: 'shield',
    labelFa: 'حسابرسی و دفترکل',
    path: '/audit',
    placement: 'sidebar',
    ready: true,
  },
  // The Marketer's own read-only view (D-28); the prototype is the staff panel and has no item for it.
  {
    area: 'myCommissions',
    heading: { eyebrow: 'بازاریاب', title: 'کمیسیون‌های من' },
    icon: 'share',
    labelFa: 'کمیسیون‌های من',
    path: '/my-commissions',
    placement: 'sidebar',
    ready: true,
  },
  {
    area: 'writeOffs',
    heading: { eyebrow: 'تعهد مستقل از وصول خریدار', title: 'سوخت مطالبات' },
    icon: 'alert',
    labelFa: 'سوخت مطالبات',
    path: '/write-offs',
    placement: 'more',
    ready: true,
  },
  {
    area: 'calendar',
    heading: { eyebrow: 'روزهای کاری تسویه با فروشگاه‌ها', title: 'تقویم کاری' },
    icon: 'calendar',
    labelFa: 'تقویم کاری',
    path: '/calendar',
    placement: 'more',
    ready: true,
  },
  {
    area: 'merchants',
    heading: {
      description: 'هر فروشگاه یک دسته‌بندی اصلی فعال دارد. دسته‌بندی‌ها درختی‌اند.',
      eyebrow: 'پذیرندگان',
      title: 'دسته‌بندی فروشگاه‌ها',
    },
    icon: 'doc',
    labelFa: 'دسته‌بندی فروشگاه‌ها',
    path: '/merchant-categories',
    placement: 'inside',
    ready: true,
  },
  // «خروجی» on the merchants toolbar (prototype); exports arrive with AC §11.17.
  {
    area: 'merchants',
    heading: { eyebrow: 'پذیرندگان', title: 'خروجی فهرست فروشگاه‌ها' },
    icon: 'download',
    labelFa: 'خروجی‌ها',
    path: '/exports',
    placement: 'inside',
  },
  {
    area: 'me',
    heading: {
      description: 'نقش‌ها، دسترسی‌ها و روش ورود به پنل مدیریت.',
      eyebrow: 'تنظیمات شخصی',
      title: 'حساب من',
    },
    icon: 'user',
    labelFa: 'حساب من',
    path: '/me',
    placement: 'more',
    ready: true,
  },
];

/** The routes the principal may see; before `/me` loads (or when it fails) only «حساب من». */
export function visibleRoutes(self: AdminSelf | undefined): readonly AdminRoute[] {
  return ADMIN_ROUTES.filter(
    (route) => route.area === 'me' || (self !== undefined && canSeeArea(self, route.area)),
  );
}

export function isRouteReady(route: AdminRoute): boolean {
  return route.ready === true;
}

/** Routes that may appear in navigation or deep links (XAP-04, Q9). */
export function navigableRoutes(self: AdminSelf | undefined): readonly AdminRoute[] {
  return visibleRoutes(self).filter((route) => isRouteReady(route));
}

/** The sidebar items: the prototype's, in its order, that the principal may see. */
export function sidebarRoutes(self: AdminSelf | undefined): readonly AdminRoute[] {
  return navigableRoutes(self).filter((route) => route.placement === 'sidebar');
}

/** The phone «بیشتر» sheet: every navigable destination except the ones opened from a screen. */
export function moreRoutes(self: AdminSelf | undefined): readonly AdminRoute[] {
  return navigableRoutes(self).filter((route) => route.placement !== 'inside');
}

export function routeFor(path: string): AdminRoute | undefined {
  return ADMIN_ROUTES.find((route) => route.path === path);
}

/** Sidebar label for the approvals inbox, with an optional decidable count (ADM-01). */
/**
 * «درخواست‌ها» with what waits on the caller: approvals they may decide plus open merchant requests
 * they may act on; `more` adds «+» when a merchant list has a further page.
 */
export function approvalsNavLabel(count: number, more = false): string {
  return count > 0 || more ? `درخواست‌ها (${formatCount(count)}${more ? '+' : ''})` : 'درخواست‌ها';
}

/** Up to three phone bar destinations derived from what the role may open (ADM-01). */
export function phoneBarRoutes(self: AdminSelf | undefined): readonly AdminRoute[] {
  const sidebar = sidebarRoutes(self);
  const byPath = new Map(sidebar.map((route) => [route.path, route]));
  const preferred = ['/dashboard', '/merchants', '/approvals'] as const;
  const picked: AdminRoute[] = [];
  for (const path of preferred) {
    const route = byPath.get(path);
    if (route !== undefined) picked.push(route);
  }
  for (const route of sidebar) {
    if (picked.length >= 3) break;
    if (!picked.some((item) => item.path === route.path)) picked.push(route);
  }
  return picked.slice(0, 3);
}

/** Merchant detail tab keys (ADM-15): synced to the `tab` query param. */
export type MerchantDetailTab = 'overview' | 'commercial' | 'people' | 'payout';

export function merchantDetailPath(
  merchantId: string,
  options?: { readonly search?: string; readonly tab?: MerchantDetailTab },
): string {
  const params = new URLSearchParams(options?.search ?? '');
  if (options?.tab !== undefined && options.tab !== 'overview') {
    params.set('tab', options.tab);
  } else if (options?.tab === 'overview') {
    params.delete('tab');
  }
  const qs = params.toString();
  return `/merchants/${encodeURIComponent(merchantId)}${qs === '' ? '' : `?${qs}`}`;
}

/** Targeting sheet path with optional organization and reason query (ADM-51, ADM-64, ADM-71). */
export function creditProgramTargetingPath(
  programId: string,
  query?: { readonly organizationId?: string; readonly reason?: string },
): string {
  const params = new URLSearchParams();
  if (query?.organizationId !== undefined && query.organizationId !== '') {
    params.set('organizationId', query.organizationId);
  }
  if (query?.reason !== undefined && query.reason !== '') {
    params.set('reason', query.reason);
  }
  const search = params.toString();
  return `/credit-programs/${programId}/targeting${search === '' ? '' : `?${search}`}`;
}

/** A tab of «کارت‌های فیزیکی» (D-117): the summary is the page itself, the rest are sub-paths. */
export function physicalCardsPath(tab: PhysicalCardsTab = 'summary'): string {
  return tab === 'summary' ? '/physical-cards' : `/physical-cards/${tab}`;
}
